import { createContext, useMemo } from 'react'

import { useLocalization } from '../../../../../../../../app/_providers/LocalizationProvider.client'
import { filterKeysFromFilterConfig } from '../../../../../../../../lib/graphql/queries/FETCH_COLLECTION_LAYOUT'
import { createContextHook, deriveMapFromList } from '../../../../../../../../lib/utils'
import { upperFirstLocalized } from '../../../../../../../../lib/utils/collection-filters'
import { CollectionItemFilterOutput } from '../../../../../../../../types/graphql/collection-view'
import { KnownFilterKeys } from '../../../../../../../constants/collections'

export const CollectionFiltersContext = createContext<
  | {
      filters: CollectionItemFilterOutput | undefined | null
      filterConfig: Parameters<typeof filterKeysFromFilterConfig>[0]
      totalItemsCount: number
    }
  | undefined
>(undefined)

const useCollectionFiltersContextImpl = createContextHook(
  CollectionFiltersContext,
  'CollectionFiltersContext'
)

export const useCollectionFiltersContext = () => {
  const rawContext = useCollectionFiltersContextImpl()
  const { locale } = useLocalization()

  const filters = useMemo(() => {
    const filterConfigKeySortOrder = rawContext.filterConfig
      ? filterKeysFromFilterConfig(rawContext.filterConfig)
      : null
    return rawContext.filters
      ? rawContext.filters
          .filter(filter => {
            // Enforce filter config filter selections if provided
            return rawContext.filterConfig
              ? rawContext.filterConfig.filters.find(
                  filterConfig => filterConfig.name === filter.name
                )
              : true
          })
          .sort((filterA, filterB) => {
            // Enforce filter config sort order if provided
            if (!filterConfigKeySortOrder) return 0
            const orderA = filterConfigKeySortOrder.indexOf(filterA.name)
            const orderB = filterConfigKeySortOrder.indexOf(filterB.name)
            return orderA - orderB
          })
          .map(filter => {
            // Merge crawlable info from config with filters for convenience downstream
            return {
              ...filter,
              // Catalog API returns lowercase length values (e.g. "petite").
              values:
                filter.name === KnownFilterKeys.Length
                  ? filter.values.map(filterValue => ({
                      ...filterValue,
                      displayValue: upperFirstLocalized(filterValue.displayValue, locale),
                    }))
                  : filter.values,
              crawlable: rawContext.filterConfig
                ? Boolean(
                    rawContext.filterConfig.filters.find(
                      filterConfig => filterConfig.name === filter.name && filterConfig.crawlable
                    )
                  )
                : false,
            }
          })
      : undefined
  }, [locale, rawContext])

  // Derive record of colors from filters for convenience downstream
  const colors = useMemo(() => {
    const colorFilterValues = rawContext.filters
      ?.map(filter => filter.values)
      .flat()
      .filter(v => v.__typename === 'ProductCatalogProductColorFilterValue')
      .map(v => v.color)
    return deriveMapFromList(colorFilterValues ?? [], curr => curr.id)
  }, [rawContext])

  return useMemo(() => {
    return {
      totalItemsCount: rawContext.totalItemsCount,
      filters,
      colors,
    }
  }, [colors, filters, rawContext.totalItemsCount])
}
